import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
/**
 * @page AdminLogsPage
 * @description 系統日誌查詢頁（UI-7-1）
 * 支援等級/分類/關鍵字過濾 + 日期範圍 + 分頁
 */
import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { io, Socket } from 'socket.io-client';
import { api } from '../../lib/api.ts';
import { employeeSocketSession } from '../../lib/employeeSocketSession';
import { SocketSessionRetry } from '../../components/SocketSessionRetry';
import { safeFormatDate } from '../../lib/format';
import { useAuthStore } from '../../store/auth.store.ts';
import type { PaginatedResponse, SystemLogDto, LiveSystemLogDto } from '@pokerroom/types';

const LOG_LEVELS = ['INFO', 'WARNING', 'ERROR', 'CRITICAL', 'DEBUG', 'VERBOSE'] as const;
const LOG_CATEGORIES = [
  'AUTH',
  'MEMBER',
  'ACCOUNTING',
  'TOURNAMENT',
  'SYSTEM',
  'SECURITY',
  'ADMIN',
  'DISPLAY',
] as const;

const LEVEL_COLORS: Record<string, string> = {
  INFO: 'bg-info/15 text-info',
  WARNING: 'bg-warning/15 text-warning',
  ERROR: 'bg-error/15 text-error',
  CRITICAL: 'bg-error/30 text-error font-bold',
  DEBUG: 'bg-base-300 text-base-content/40',
  VERBOSE: 'bg-base-300 text-base-content/30',
};

// This decoded identity only gates presentation; the server validates the signed token.
export function logViewerIdentity(token: string | null, userId: string | undefined) {
  if (!token || !userId) return null;
  try {
    const encoded = token.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/');
    if (!encoded) return null;
    const payload = JSON.parse(atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '=')));
    if (
      payload.sub !== userId ||
      payload.type !== 'access' ||
      typeof payload.exp !== 'number' ||
      !Number.isFinite(payload.exp) ||
      typeof payload.venueId !== 'string' ||
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(payload.venueId) ||
      !Array.isArray(payload.roleKeys) ||
      !payload.roleKeys.includes('SUPER_ADMIN')
    )
      return null;
    return { userId, venueId: payload.venueId as string };
  } catch {
    return null;
  }
}

function AdminLogsPage() {
  const { t } = useTranslation('admin');
  const [level, setLevel] = useState('');
  const [category, setCategory] = useState('');
  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const limit = 30;

  // Live Mode
  const [liveMode, setLiveMode] = useState(false);
  const [liveBuffer, setLiveBuffer] = useState<{ scope: string; logs: LiveSystemLogDto[] } | null>(
    null,
  );
  const [deniedScope, setDeniedScope] = useState<string | null>(null);
  const [sessionPaused, setSessionPaused] = useState(false);
  const sessionRef = useRef<ReturnType<typeof employeeSocketSession> | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const revision = useAuthStore((s) => s.authRevision);
  const userId = useAuthStore((s) => s.user?.id);
  const authenticated = useAuthStore((s) => s.isAuthenticated);
  const token = useAuthStore((s) => s.accessToken);

  const viewer = authenticated ? logViewerIdentity(token, userId) : null;
  const canRead = Boolean(viewer);
  const scope = viewer ? JSON.stringify([viewer.userId, viewer.venueId, revision]) : null;
  const liveLogs = canRead && scope && liveBuffer?.scope === scope ? liveBuffer.logs : [];
  const permissionDenied = !viewer || deniedScope === scope;

  useEffect(() => {
    if (!scope) setLiveMode(false);
    if (!liveMode || !token || !scope || permissionDenied) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      return;
    }
    const wsBase =
      (import.meta as unknown as { env: { VITE_WS_URL?: string } }).env.VITE_WS_URL ?? '';
    const socket = io(`${wsBase}/logs`, {
      withCredentials: true,
      auth: { token },
      transports: ['websocket'],
    });
    socketRef.current = socket;
    const session = employeeSocketSession(socket, token, (paused) => {
      setSessionPaused(paused);
      if (paused) setLiveBuffer(null);
    });
    sessionRef.current = session;
    setSessionPaused(false);
    const current = () => session.canAct() && useAuthStore.getState().authRevision === revision;
    const deny = () => {
      if (!session.current() || useAuthStore.getState().authRevision !== revision) return;
      setDeniedScope(scope);
      setLiveMode(false);
      setLiveBuffer(null);
      setSessionPaused(false);
      session.dispose();
    };
    socket.on('log:denied', (notice: { code?: string }) => {
      if (notice?.code === 'LOG_PERMISSION_DENIED') deny();
    });
    socket.on('connect_error', (error: Error & { data?: { code?: string } }) => {
      if (error?.data?.code === 'LOG_PERMISSION_DENIED') deny();
    });
    socket.on('log:new', (log: LiveSystemLogDto) => {
      if (!current()) return;
      setLiveBuffer((prev) => ({
        scope,
        logs: [log, ...(prev?.scope === scope ? prev.logs : [])].slice(0, 100),
      }));
    });
    return () => {
      session.dispose();
      if (socketRef.current === socket) {
        socketRef.current = null;
        sessionRef.current = null;
      }
    };
  }, [liveMode, token, revision, scope, permissionDenied]);

  // Debounce keyword to avoid firing API on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKeyword(keyword);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [keyword]);

  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (level) params.set('level', level);
  if (category) params.set('category', category);
  if (debouncedKeyword) params.set('keyword', debouncedKeyword);
  if (startDate) params.set('startDate', new Date(startDate + 'T00:00:00').toISOString());
  if (endDate) params.set('endDate', new Date(endDate + 'T23:59:59').toISOString());

  const { data, isLoading } = useQuery<PaginatedResponse<SystemLogDto>>({
    queryKey: [
      'logs',
      viewer?.userId,
      viewer?.venueId,
      revision,
      level,
      category,
      debouncedKeyword,
      startDate,
      endDate,
      page,
    ],
    queryFn: () => api.get(`/logs?${params}`),
    enabled: canRead,
  });

  const logs = canRead ? (data?.items ?? []) : [];
  const total = canRead ? (data?.total ?? 0) : 0;
  const totalPages = Math.ceil(total / limit);
  const hasFilters = !!(level || category || keyword || startDate || endDate);

  const resetFilters = () => {
    setLevel('');
    setCategory('');
    setKeyword('');
    setDebouncedKeyword('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  return (
    <AdminPage className="admin-page-body [&_button]:min-h-11 [&_button]:text-sm [&_input:not([type=checkbox])]:min-h-11 [&_input:not([type=checkbox])]:text-base [&_select]:min-h-11 [&_select]:text-base">
      {permissionDenied && (
        <p role="alert" className="text-error mb-4">
          {t('logAccessDenied')}
        </p>
      )}
      <SocketSessionRetry
        paused={Boolean(viewer) && !permissionDenied && sessionPaused}
        retry={() => sessionRef.current?.retry()}
      />
      <AdminPageHeader
        title={t('systemLogs')}
        description={t('logSubtitle')}
        actions={
          <>
            <div className="flex items-center gap-3">
              <button
                disabled={permissionDenied || !canRead}
                onClick={() => {
                  setLiveMode(!liveMode);
                  if (!liveMode) setLiveBuffer(null);
                }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  liveMode && !permissionDenied
                    ? 'bg-success/20 text-success'
                    : 'bg-base-300 text-base-content/60 hover:bg-base-300/80'
                }`}
              >
                <span
                  className={`w-2 h-2 rounded-full ${liveMode && !permissionDenied ? 'bg-success animate-pulse' : 'bg-base-content/30'}`}
                />
                {liveMode && !permissionDenied ? 'LIVE' : t('liveMode', { defaultValue: 'Live' })}
              </button>
              <span className="text-sm text-base-content/40 font-mono">
                {t('totalCount', { count: total })}
              </span>
            </div>
          </>
        }
      />

      {/* Filters */}
      <div className="bg-base-200 rounded-xl border border-base-300 p-4 mb-5">
        <div className="flex flex-wrap gap-3">
          <select
            value={level}
            onChange={(e) => {
              setLevel(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm focus:outline-hidden focus:ring-1 focus:ring-primary"
          >
            <option value="">{t('allLevels')}</option>
            {LOG_LEVELS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>

          <select
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm focus:outline-hidden focus:ring-1 focus:ring-primary"
          >
            <option value="">{t('allCategories')}</option>
            {LOG_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm focus:outline-hidden focus:ring-1 focus:ring-primary"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm focus:outline-hidden focus:ring-1 focus:ring-primary"
          />

          <input
            type="text"
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
            }}
            placeholder={t('searchMessages')}
            className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm focus:outline-hidden focus:ring-1 focus:ring-primary"
          />

          {hasFilters && (
            <button
              onClick={resetFilters}
              className="px-3 py-2 rounded-lg text-sm text-base-content/50 hover:text-base-content hover:bg-base-300 transition-colors"
            >
              {t('clearFilters')}
            </button>
          )}
        </div>
      </div>

      {/* Live Logs */}
      {!permissionDenied && liveMode && liveLogs.length > 0 && (
        <div className="bg-success/5 rounded-xl border border-success/20 overflow-hidden mb-4">
          <div className="px-4 py-2 bg-success/10 text-xs font-medium text-success">
            {t('liveStream', { defaultValue: 'Live Stream' })} ({liveLogs.length})
          </div>
          <div className="divide-y divide-success/10 max-h-64 overflow-y-auto">
            {liveLogs.map((log) => (
              <div key={log.id} className="px-4 py-2 animate-pulse-once">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className="text-xs font-mono text-base-content/40">
                    {safeFormatDate(log.createdAt, (d) =>
                      d.toLocaleTimeString('zh-TW', { hour12: false }),
                    )}
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium ${LEVEL_COLORS[log.level] ?? 'bg-base-300'}`}
                  >
                    {log.level}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded bg-base-300 text-base-content/50">
                    {log.category}
                  </span>
                  {log.actorUsername && (
                    <span className="text-xs text-primary/70">@{log.actorUsername}</span>
                  )}
                </div>
                <p className="text-sm text-base-content">{log.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Log List */}
      <div className="bg-base-200 rounded-xl border border-base-300 overflow-hidden">
        {isLoading ? (
          <div className="p-10 text-center text-base-content/40">{t('loading')}</div>
        ) : logs.length === 0 ? (
          <div className="p-10 text-center text-base-content/40">
            <div className="text-3xl mb-2">📋</div>
            {hasFilters ? t('noFilteredLogs') : t('noLogs')}
          </div>
        ) : (
          <div className="divide-y divide-base-300">
            {logs.map((log) => (
              <div key={log.id} className="px-4 py-3 hover:bg-base-300/30 transition-colors">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-xs font-mono text-base-content/40 shrink-0">
                    {safeFormatDate(log.createdAt, (d) =>
                      d.toLocaleString('zh-TW', {
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false,
                      }),
                    )}
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 ${LEVEL_COLORS[log.level] ?? 'bg-base-300'}`}
                  >
                    {log.level}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded bg-base-300 text-base-content/50 shrink-0">
                    {log.category}
                  </span>
                  {log.action && (
                    <span className="text-xs font-mono text-base-content/40 shrink-0">
                      {log.action}
                    </span>
                  )}
                  {log.actorUsername && (
                    <span className="text-xs text-primary/70 shrink-0">@{log.actorUsername}</span>
                  )}
                  {log.ipAddress && (
                    <span className="text-xs text-base-content/30 font-mono shrink-0">
                      {log.ipAddress}
                    </span>
                  )}
                </div>
                <p className="text-sm text-base-content">{log.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mt-4">
          <span className="text-sm text-base-content/50">
            {t('paginationInfo', { page, totalPages })}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage(1)}
              className="hidden sm:block min-h-11 px-2 py-1.5 rounded text-sm border border-base-300 disabled:opacity-30 hover:bg-base-300"
            >
              «
            </button>
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="min-h-11 min-w-11 px-3 py-1.5 rounded text-sm border border-base-300 disabled:opacity-30 hover:bg-base-300"
            >
              ‹
            </button>
            <span className="sm:hidden text-sm px-2">
              {page} / {totalPages}
            </span>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const start = Math.max(1, Math.min(page - 2, totalPages - 4));
              const p = start + i;
              if (p > totalPages) return null;
              return (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`hidden sm:block min-h-11 px-3 py-1.5 rounded text-sm border ${p === page ? 'bg-primary text-primary-content border-primary' : 'border-base-300 hover:bg-base-300'}`}
                >
                  {p}
                </button>
              );
            })}
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="min-h-11 min-w-11 px-3 py-1.5 rounded text-sm border border-base-300 disabled:opacity-30 hover:bg-base-300"
            >
              ›
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(totalPages)}
              className="hidden sm:block min-h-11 px-2 py-1.5 rounded text-sm border border-base-300 disabled:opacity-30 hover:bg-base-300"
            >
              »
            </button>
          </div>
        </div>
      )}
    </AdminPage>
  );
}

export default AdminLogsPage;
